/**
 * Redirect URL validation utilities
 * Prevents open redirect attacks by validating all redirect URLs against a whitelist
 */

// Whitelist of allowed exact redirect paths
const ALLOWED_REDIRECT_PATHS = [
  '/',
  '/profile',
  '/my-reviews',
  '/community',
  '/community-spaces',
  '/top-users',
  '/points-market',
  '/earn-points',
  '/invitations',
  '/my-tickets',
  '/tickets',
  '/add-website',
  '/add-space',
  '/my-websites',
  '/my-spaces',
  '/my-collections',
  '/shared-collections',
  '/subscribed-collections',
  '/trends',
  '/plans',
  '/login',
  '/extension-login-success',
  '/callback',
];

// Allowed path prefixes (for dynamic routes like /spaces/:id, /website/:id, /user/:id)
const ALLOWED_PATH_PREFIXES = [
  '/spaces/',
  '/website/',
  '/user/',
];

// Dangerous protocols that should never be allowed
const DANGEROUS_PROTOCOLS = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'about:',
  'chrome:',
  'chrome-extension:',
];

/**
 * Decode URL-encoded strings to check for encoded attacks
 * @param {string} url - URL to decode
 * @returns {string} Decoded URL
 */
const decodeUrl = (url) => {
  try {
    // Decode multiple times to catch double-encoding attacks
    let decoded = url;
    for (let i = 0; i < 5; i++) {
      const prev = decoded;
      decoded = decodeURIComponent(decoded);
      if (prev === decoded) break; // No more decoding needed
    }
    return decoded;
  } catch (error) {
    // If decoding fails, return original (will be caught by other checks)
    return url;
  }
};

/**
 * Normalize URL path by removing double slashes and resolving relative paths
 * @param {string} path - Path to normalize
 * @returns {string} Normalized path
 */
const normalizePath = (path) => {
  // Remove query string and fragment for path validation
  const pathOnly = path.split('?')[0].split('#')[0];
  
  // Remove double slashes (except at the start)
  let normalized = pathOnly.replace(/\/+/g, '/');
  
  // Ensure it starts with /
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }
  
  return normalized;
};

/**
 * Check if URL contains dangerous protocols
 * @param {string} url - URL to check
 * @returns {boolean} True if dangerous protocol found
 */
const containsDangerousProtocol = (url) => {
  const lowerUrl = url.toLowerCase();
  return DANGEROUS_PROTOCOLS.some(protocol => lowerUrl.includes(protocol));
};

/**
 * Validate redirect URL to prevent open redirect attacks
 * @param {string} url - URL to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.allowQueryParams - Allow query parameters (default: true)
 * @param {boolean} options.allowFragments - Allow URL fragments (default: true)
 * @returns {boolean} True if URL is valid and safe to redirect to
 */
export const isValidRedirectUrl = (url, options = {}) => {
  const { allowQueryParams = true, allowFragments = true } = options;

  // Basic checks
  if (!url || typeof url !== 'string' || url === 'null' || url === 'undefined') {
    return false;
  }

  // Only allow relative paths (starting with /)
  // Absolute URLs (http://, https://) are not allowed
  if (!url.startsWith('/')) {
    return false;
  }

  // Decode URL to check for encoded attacks
  const decoded = decodeUrl(url);
  
  // Check for dangerous protocols (in both original and decoded)
  if (containsDangerousProtocol(url) || containsDangerousProtocol(decoded)) {
    return false;
  }

  // Normalize path for validation
  const normalizedPath = normalizePath(url);

  // Check if path is in exact whitelist
  if (ALLOWED_REDIRECT_PATHS.includes(normalizedPath)) {
    return true;
  }

  // Check if path starts with allowed prefix
  const hasAllowedPrefix = ALLOWED_PATH_PREFIXES.some(prefix => {
    if (normalizedPath.startsWith(prefix)) {
      // Extract the ID part after the prefix
      const idPart = normalizedPath.slice(prefix.length);
      
      // Validate ID format (alphanumeric, hyphens, underscores only)
      // This prevents path traversal attacks like /spaces/../admin
      if (idPart && /^[a-zA-Z0-9_-]+$/.test(idPart.split('/')[0])) {
        return true;
      }
    }
    return false;
  });

  if (hasAllowedPrefix) {
    return true;
  }

  // If we get here, the path is not in the whitelist
  return false;
};

/**
 * Sanitize and validate redirect URL
 * Returns a safe redirect URL or null if invalid
 * @param {string} url - URL to sanitize
 * @returns {string|null} Safe redirect URL or null
 */
export const sanitizeRedirectUrl = (url) => {
  if (!isValidRedirectUrl(url)) {
    return null;
  }

  // Extract path, query, and fragment
  const [path, queryFragment] = url.split('?');
  const [query, fragment] = queryFragment ? queryFragment.split('#') : [null, null];

  // Build safe URL
  let safeUrl = normalizePath(path);
  
  // Add query string if present and allowed
  if (query) {
    // Basic validation: query should not contain dangerous characters
    if (!/javascript:|data:|vbscript:/i.test(query)) {
      safeUrl += '?' + query;
    }
  }
  
  // Add fragment if present and allowed
  if (fragment) {
    // Basic validation: fragment should not contain dangerous characters
    if (!/javascript:|data:|vbscript:/i.test(fragment)) {
      safeUrl += '#' + fragment;
    }
  }

  return safeUrl;
};

/**
 * Get safe redirect URL, falling back to default if invalid
 * @param {string} url - URL to validate
 * @param {string} defaultUrl - Default URL to use if invalid (default: '/')
 * @returns {string} Safe redirect URL
 */
export const getSafeRedirectUrl = (url, defaultUrl = '/') => {
  const sanitized = sanitizeRedirectUrl(url);
  return sanitized || defaultUrl;
};
